import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Wallet, TrendingDown, TrendingUp, AlertTriangle, Save,
  CalendarDays, Info, RefreshCw, Receipt, Sparkles, Repeat,
  Pencil, EyeOff, Eye,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";
import EditRecurringPatternDialog from "./EditRecurringPatternDialog";

const HORIZONS = [30, 60, 90];

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

export default function CashFlowForecast({ restaurantId }) {
  const [settings, setSettings] = useState({ starting_balance: 0, alert_threshold: 0 });
  const [balanceInput, setBalanceInput] = useState("");
  const [thresholdInput, setThresholdInput] = useState("");
  const [horizon, setHorizon] = useState(30);
  const [forecast, setForecast] = useState(null);
  const [disabledPatterns, setDisabledPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadAll = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [s, f, rec] = await Promise.all([
        api.getCashFlowSettings(restaurantId),
        api.getCashFlowForecast(restaurantId, { horizon }),
        api.getRecurringExpenses(restaurantId, { horizon: Math.max(horizon, 90) }).catch(() => ({ disabled_patterns: [] })),
      ]);
      setSettings(s);
      setBalanceInput(s.starting_balance ? String(s.starting_balance) : "");
      setThresholdInput(s.alert_threshold ? String(s.alert_threshold) : "");
      setForecast(f);
      setDisabledPatterns(rec.disabled_patterns || []);
    } catch (e) {
      toast.error(e.message || "Error cargando previsión");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, horizon]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePatternsChanged = useCallback(async () => {
    // Refetch just the forecast + recurring lists — no need to reload settings.
    try {
      const [f, rec] = await Promise.all([
        api.getCashFlowForecast(restaurantId, { horizon }),
        api.getRecurringExpenses(restaurantId, { horizon: Math.max(horizon, 90) }),
      ]);
      setForecast(f);
      setDisabledPatterns(rec.disabled_patterns || []);
    } catch (e) {
      toast.error(e.message || "No se pudo refrescar la previsión");
    }
  }, [restaurantId, horizon]);

  const saveSettings = async () => {
    const sb = balanceInput.trim() === "" ? 0 : parseFloat(balanceInput);
    const th = thresholdInput.trim() === "" ? 0 : parseFloat(thresholdInput);
    if (Number.isNaN(sb) || Number.isNaN(th) || sb < 0 || th < 0) {
      toast.error("Introduce números válidos no negativos");
      return;
    }
    setSavingSettings(true);
    try {
      const r = await api.setCashFlowSettings(restaurantId, {
        starting_balance: sb, alert_threshold: th,
      });
      setSettings(r);
      toast.success("Parámetros guardados");
      // Re-run forecast with new settings
      const f = await api.getCashFlowForecast(restaurantId, { horizon });
      setForecast(f);
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSavingSettings(false);
    }
  };

  const settingsDirty =
    (parseFloat(balanceInput || "0") !== (settings.starting_balance || 0)) ||
    (parseFloat(thresholdInput || "0") !== (settings.alert_threshold || 0));

  if (loading && !forecast) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="cash-flow-forecast">
      {/* Settings row */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold flex items-center gap-1.5">
                <Wallet className="h-4 w-4 text-primary" /> Parámetros de previsión
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Introduce tu saldo actual de caja + banco y el umbral de alerta mínimo.
                No conectamos con tu banco: tú controlas el saldo inicial.
              </p>
            </div>
            <div className="flex gap-1 rounded-lg border bg-muted/50 p-0.5">
              {HORIZONS.map(h => (
                <button
                  key={h}
                  onClick={() => setHorizon(h)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                    horizon === h
                      ? "bg-card shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-slate-700"
                  }`}
                  data-testid={`horizon-${h}`}
                >
                  {h}d
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="starting-balance" className="text-xs font-medium">
                Saldo inicial (caja + banco)
              </Label>
              <div className="relative">
                <Input
                  id="starting-balance"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  value={balanceInput}
                  onChange={(e) => setBalanceInput(e.target.value)}
                  className="pr-8"
                  data-testid="cash-flow-starting-balance"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alert-threshold" className="text-xs font-medium">
                Umbral de alerta <span className="text-muted-foreground font-normal">(0 = sin alerta)</span>
              </Label>
              <div className="relative">
                <Input
                  id="alert-threshold"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  className="pr-8"
                  data-testid="cash-flow-threshold"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
              </div>
            </div>
          </div>

          {settingsDirty && (
            <div className="flex items-center justify-end">
              <Button
                size="sm"
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="cash-flow-save-settings"
              >
                {savingSettings
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Save className="h-3.5 w-3.5 mr-1.5" />}
                Guardar y recalcular
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {forecast && (
        <>
          {/* Alert banner */}
          {forecast.alert_triggered && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-3"
                 data-testid="cash-flow-alert-banner">
              <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-900">
                  Previsión por debajo del umbral ({fmtEUR(forecast.alert_threshold)})
                </div>
                <div className="text-xs text-red-800/80 mt-0.5">
                  El saldo proyectado caerá a <b>{fmtEUR(forecast.min_balance)}</b> el {fmtDate(forecast.min_balance_date)}.
                  Considera adelantar cobros o posponer pagos no críticos.
                </div>
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile
              tone="slate"
              icon={Wallet}
              label="Saldo inicial"
              value={fmtEUR(forecast.starting_balance)}
              sub={`hoy`}
              testid="kpi-starting"
            />
            <KpiTile
              tone={forecast.min_balance >= (forecast.alert_threshold || 0) ? "emerald" : "red"}
              icon={TrendingDown}
              label="Saldo mínimo"
              value={fmtEUR(forecast.min_balance)}
              sub={fmtDate(forecast.min_balance_date)}
              testid="kpi-min"
            />
            <KpiTile
              tone="sky"
              icon={TrendingUp}
              label={`Entradas ${forecast.horizon_days}d`}
              value={fmtEUR(forecast.total_inflow)}
              sub={`~${fmtEUR(forecast.avg_daily_inflow)}/día`}
              testid="kpi-in"
            />
            <KpiTile
              tone={forecast.ending_balance >= forecast.starting_balance ? "emerald" : "amber"}
              icon={Sparkles}
              label={`Saldo en ${forecast.horizon_days}d`}
              value={fmtEUR(forecast.ending_balance)}
              sub={`Δ ${forecast.ending_balance >= forecast.starting_balance ? "+" : ""}${fmtEUR(forecast.ending_balance - forecast.starting_balance)}`}
              testid="kpi-ending"
              emphasis
            />
          </div>

          {/* Chart + upcoming payments side by side */}
          <div className="grid lg:grid-cols-5 gap-3">
            <div className="lg:col-span-3">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-1.5">
                      <CalendarDays className="h-4 w-4" /> Proyección de saldo
                    </h3>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={loadAll}
                      disabled={loading}
                      data-testid="cash-flow-refresh"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                  <BalanceChart forecast={forecast} />
                  <div className="flex items-center gap-4 text-[10px] text-muted-foreground justify-center pt-1">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded bg-indigo-500" /> Saldo proyectado</span>
                    {forecast.alert_threshold > 0 && (
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded border-t border-dashed border-red-400" /> Umbral</span>
                    )}
                    <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Pago conocido</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <UpcomingPayments forecast={forecast} />
            </div>
          </div>

          {((forecast.recurring_patterns && forecast.recurring_patterns.length > 0)
            || disabledPatterns.length > 0) && (
            <RecurringPatterns
              patterns={forecast.recurring_patterns || []}
              disabledPatterns={disabledPatterns}
              restaurantId={restaurantId}
              onChanged={handlePatternsChanged}
            />
          )}

          {/* Disclaimer */}
          <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/50 border border-border rounded-lg p-3">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              La previsión extrapola tus <b>últimos 90 días</b> de ventas y OPEX y superpone las
              facturas pendientes con vencimiento en los próximos {forecast.horizon_days} días.
              No incluye eventos excepcionales (nóminas extras, impuestos trimestrales, etc.) que no estén registrados.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────

function KpiTile({ tone, icon: Icon, label, value, sub, emphasis, testid }) {
  const tones = {
    slate:   "from-slate-50 to-slate-100 border-border text-foreground",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    sky:     "from-sky-50 to-sky-100 border-sky-200 text-sky-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    red:     "from-red-50 to-red-100 border-red-200 text-red-900",
  };
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${tones[tone]} p-4 shadow-sm ${emphasis ? "ring-2 ring-offset-1 ring-current/20" : ""}`}
      data-testid={testid}
    >
      <Icon className="h-5 w-5 opacity-80 mb-2" />
      <div className="text-2xl font-bold tracking-tight">{value}</div>
      <div className="text-xs opacity-80 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function BalanceChart({ forecast }) {
  const { series, alert_threshold: threshold } = forecast;
  const W = 560, H = 200, P = 30;
  const n = Math.max(series.length, 1);

  const balances = series.map(s => s.balance);
  const minB = Math.min(0, ...balances, threshold || 0);
  const maxB = Math.max(...balances, threshold || 0);
  const range = maxB - minB || 1;

  const xOf = (i) => P + ((W - 2 * P) * i) / (n - 1 || 1);
  const yOf = (v) => H - P - ((v - minB) / range) * (H - 2 * P);

  const path = series.map((s, i) =>
    `${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(s.balance).toFixed(1)}`
  ).join(" ");

  const areaPath = `${path} L${xOf(n - 1).toFixed(1)},${yOf(minB).toFixed(1)} L${xOf(0).toFixed(1)},${yOf(minB).toFixed(1)} Z`;

  const thresholdY = threshold > 0 ? yOf(threshold) : null;
  const zeroY = minB < 0 ? yOf(0) : null;

  // Label every ~5th day
  const stride = Math.max(1, Math.round(n / 8));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48" data-testid="cash-flow-chart">
      <defs>
        <linearGradient id="cf-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.24" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Grid line at 0 if applicable */}
      {zeroY !== null && (
        <line x1={P} x2={W - P} y1={zeroY} y2={zeroY}
              className="stroke-slate-300" strokeDasharray="2 3" strokeWidth="0.6" />
      )}
      {/* Threshold line */}
      {thresholdY !== null && (
        <>
          <line x1={P} x2={W - P} y1={thresholdY} y2={thresholdY}
                className="stroke-red-400" strokeDasharray="3 3" strokeWidth="1" />
          <text x={W - P - 2} y={thresholdY - 3} textAnchor="end"
                className="fill-red-500" fontSize="9">
            Umbral
          </text>
        </>
      )}

      {/* Area + line */}
      <path d={areaPath} fill="url(#cf-grad)" />
      <path d={path} className="stroke-indigo-500" fill="none" strokeWidth="1.6" strokeLinejoin="round" />

      {/* Event dots */}
      {series.map((s, i) => (
        s.events && s.events.length > 0 && (
          <g key={`ev-${i}`}>
            <circle cx={xOf(i)} cy={yOf(s.balance)} r="3.5"
                    className="fill-amber-500 stroke-white" strokeWidth="1.5">
              <title>{s.date}: {s.events.map(e => `${e.label} (${e.sign === "out" ? "-" : "+"}${e.amount.toFixed(0)}€)`).join(" · ")}</title>
            </circle>
          </g>
        )
      ))}

      {/* X-axis date labels */}
      {series.map((s, i) => (
        i % stride === 0 && (
          <text key={`lbl-${i}`} x={xOf(i)} y={H - 8} textAnchor="middle"
                className="fill-slate-400" fontSize="9">
            {fmtDate(s.date)}
          </text>
        )
      ))}

      {/* Starting + ending labels */}
      <circle cx={xOf(0)} cy={yOf(series[0].balance)} r="3.5"
              className="fill-indigo-600 stroke-white" strokeWidth="1.5" />
      <circle cx={xOf(n - 1)} cy={yOf(series[n - 1].balance)} r="3.5"
              className="fill-indigo-600 stroke-white" strokeWidth="1.5" />
    </svg>
  );
}

function UpcomingPayments({ forecast }) {
  const events = useMemo(() => {
    const out = [];
    for (const s of forecast.series) {
      for (const e of (s.events || [])) {
        out.push({ ...e, date: s.date });
      }
    }
    return out
      .filter(e => e.sign === "out")
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 12);
  }, [forecast]);

  return (
    <Card className="h-full">
      <CardContent className="p-5 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          <Receipt className="h-4 w-4" /> Pagos previstos
          {events.length > 0 && (
            <span className="inline-flex items-center justify-center text-[10px] font-semibold min-w-[18px] h-[18px] rounded-full bg-amber-100 text-amber-800 px-1.5">
              {events.length}
            </span>
          )}
        </h3>
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3">
            No hay facturas pendientes ni patrones recurrentes en los próximos {forecast.horizon_days} días.
          </p>
        ) : (
          <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="upcoming-payments-list">
            {events.map((e) => {
              const isRecurring = e.type === "recurring";
              return (
                <li key={`${e.ref_id}-${e.date}`}
                    className="flex items-start gap-2 text-sm"
                    data-testid={`upcoming-payment-${e.ref_id}`}>
                  <div className={`flex flex-col items-center shrink-0 w-10 rounded-md border py-1 ${
                    isRecurring ? "bg-indigo-50 border-indigo-200" : "bg-muted/50"
                  }`}>
                    <span className={`text-[10px] uppercase leading-tight ${isRecurring ? "text-indigo-600" : "text-muted-foreground"}`}>
                      {new Date(e.date).toLocaleDateString("es-ES", { month: "short" })}
                    </span>
                    <span className={`text-sm font-bold leading-tight ${isRecurring ? "text-indigo-700" : "text-foreground"}`}>
                      {new Date(e.date).getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate flex items-center gap-1.5">
                      {isRecurring && <Repeat className="h-3 w-3 text-indigo-500 shrink-0" />}
                      {e.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {isRecurring ? `Recurrente · ${e.reason}` : e.reason}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-red-700 shrink-0">
                    -{fmtEUR(e.amount)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RecurringPatterns({ patterns, disabledPatterns = [], restaurantId, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [reactivating, setReactivating] = useState(null);

  const totalMonthly = patterns
    .filter(p => p.cadence === "monthly")
    .reduce((s, p) => s + p.amount, 0);
  const totalQuarterly = patterns
    .filter(p => p.cadence === "quarterly")
    .reduce((s, p) => s + p.amount, 0);

  const cadenceBadge = {
    monthly: { label: "Mensual", cls: "bg-indigo-100 text-indigo-800" },
    quarterly: { label: "Trimestral", cls: "bg-violet-100 text-violet-800" },
    weekly: { label: "Semanal", cls: "bg-sky-100 text-sky-800" },
  };

  const handleReactivate = async (pattern) => {
    setReactivating(pattern.key);
    try {
      await api.upsertRecurringOverride(restaurantId, pattern.key, { disabled: false });
      toast.success("Patrón reactivado");
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "No se pudo reactivar");
    } finally {
      setReactivating(null);
    }
  };

  return (
    <Card data-testid="recurring-patterns-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-1.5">
              <Repeat className="h-4 w-4 text-indigo-600" />
              Pagos recurrentes detectados
              <span className="inline-flex items-center justify-center text-[10px] font-semibold min-w-[18px] h-[18px] rounded-full bg-indigo-100 text-indigo-800 px-1.5">
                {patterns.length}
              </span>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Detectados automáticamente de tu histórico (últimos 270 días). Puedes editar el importe,
              renombrarlos o desactivarlos si ya no aplican.
            </p>
          </div>
          <div className="flex gap-2 text-xs">
            {totalMonthly > 0 && (
              <span className="rounded-md bg-indigo-50 border border-indigo-200 px-2 py-1 text-indigo-800">
                <b>{fmtEUR(totalMonthly)}</b>/mes
              </span>
            )}
            {totalQuarterly > 0 && (
              <span className="rounded-md bg-violet-50 border border-violet-200 px-2 py-1 text-violet-800">
                <b>{fmtEUR(totalQuarterly)}</b>/trimestre
              </span>
            )}
          </div>
        </div>
        {patterns.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {patterns.map((p) => {
              const cad = cadenceBadge[p.cadence] || cadenceBadge.monthly;
              return (
                <div key={p.key}
                     className="rounded-lg border p-3 bg-gradient-to-br from-slate-50 to-white hover:shadow-sm transition group"
                     data-testid={`recurring-pattern-${p.key.replace(/::/g, "-")}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-foreground truncate flex items-center gap-1.5">
                        {p.supplier_name || "Sin proveedor"}
                        {p.has_override && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-amber-300 text-amber-700 bg-amber-50">
                            editado
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate capitalize">
                        {p.category}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${cad.cls}`}>
                        {cad.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-60 group-hover:opacity-100 transition-opacity"
                        onClick={() => setEditing(p)}
                        data-testid={`edit-pattern-${p.key.replace(/::/g, "-")}`}
                        aria-label="Editar patrón"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-end justify-between">
                    <div className="text-xl font-bold text-foreground leading-none">
                      {fmtEUR(p.amount)}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-muted-foreground">Próximo</div>
                      <div className="text-xs font-medium text-foreground">
                        {p.next_occurrences[0] ? fmtDate(p.next_occurrences[0]) : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{p.sample_count} repeticiones</span>
                    <span className="flex items-center gap-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                        p.confidence >= 0.75 ? "bg-emerald-500" :
                        p.confidence >= 0.5 ? "bg-amber-500" : "bg-slate-400"
                      }`} />
                      {Math.round(p.confidence * 100)}% confianza
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground italic py-2">
            Ningún patrón activo. Si antes había alguno, mira más abajo si lo desactivaste.
          </div>
        )}

        {/* Disabled patterns — collapsible list with reactivate shortcut */}
        {disabledPatterns.length > 0 && (
          <details className="group pt-2 border-t">
            <summary className="cursor-pointer text-xs font-medium flex items-center gap-1.5 text-muted-foreground hover:text-foreground select-none py-1">
              <EyeOff className="w-3.5 h-3.5" />
              Patrones desactivados ({disabledPatterns.length})
              <span className="text-[10px] normal-case font-normal">· click para ver</span>
            </summary>
            <div className="mt-2 space-y-1.5">
              {disabledPatterns.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center gap-2 p-2 rounded-md border bg-muted/50/40"
                  data-testid={`disabled-pattern-${p.key.replace(/::/g, "-")}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate line-through text-muted-foreground">
                      {p.supplier_name || "Sin proveedor"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {fmtEUR(p.amount)} · {p.cadence === "monthly" ? "mensual" : "trimestral"}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={reactivating === p.key}
                    onClick={() => handleReactivate(p)}
                    data-testid={`reactivate-pattern-${p.key.replace(/::/g, "-")}`}
                  >
                    {reactivating === p.key ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Eye className="w-3 h-3 mr-1.5" />
                        Reactivar
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>

      <EditRecurringPatternDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        pattern={editing}
        restaurantId={restaurantId}
        onSaved={onChanged}
      />
    </Card>
  );
}
